// ======================================================================
// FILE:
// src/modules/finance/transactions/payables/pages/PayablesListPage.js
//
// MODULE:
// Finance & Accounts Management System (FBAMS)
//
// PAGE:
// Payables
//
// PURPOSE:
// UI-only first version of the Accounts Payable settlement workbench.
//
// IMPORTANT:
// - No backend API is connected yet.
// - Mock data is used only to validate the screen layout.
// - Settlement/accounting logic is intentionally NOT implemented here.
// - The design supports future full, partial and multi-invoice settlement.
// ======================================================================

// ======================================================================
// PAYABLES LIST PAGE
// ======================================================================

import { useNavigate } from "react-router-dom";

import React, {
    useEffect,
    useMemo,
    useState,
} from "react";

import {
    listPayables,
    listVendorAdvancePayables,
    listTransportPayables,
    getPayable,
    createPaymentForPayable,
    createPaymentForTransportPayable,

    createPaymentForVendorAdvance,
    listAdjustableVendorAdvances,
    createVendorAdvanceAdjustment,
} from "../services/payablesService";

import AsyncSelect from "react-select/async";
import financeLookupService from "../../../services/financeLookupService";

// ======================================================================
// API DATA
// ======================================================================

// Payables are loaded from the Finance Payables API.


// ======================================================================
// HELPERS
// ======================================================================


const formatCurrency = (
    value,
    currency = "INR"
) => {
    const amount = Number(value || 0);

    const safeCurrency =
        typeof currency === "string" &&
        /^[A-Z]{3}$/.test(currency)
            ? currency
            : "INR";

    return new Intl.NumberFormat(
        "en-IN",
        {
            style: "currency",
            currency: safeCurrency,
            maximumFractionDigits: 2,
        }
    ).format(amount);
};


const formatCurrency_old1 = (
    value,
    currency = "INR"
) => {
    const amount = Number(value || 0);

    return new Intl.NumberFormat(
        "en-IN",
        {
            style: "currency",
            currency,
            maximumFractionDigits: 2,
        }
    ).format(amount);
};


const formatDate = (
    value
) => {
    if (!value) {
        return "-";
    }

    try {
        return new Date(value).toLocaleDateString(
            "en-IN",
            {
                day: "2-digit",
                month: "short",
                year: "numeric",
            }
        );
    } catch {
        return value;
    }
};


const getStatusClass = (
    status
) => {

    switch (status) {

        case "PAID":
            return "bg-success";

        case "PARTIALLY_PAID":
            return "bg-warning text-dark";

        case "UNPAID":
            return "bg-danger";

        default:
            return "bg-secondary";
    }
};


// ======================================================================
// COMPONENT
// ======================================================================

export default function PayablesListPage() {

    const [
        payables,
        setPayables,
    ] = useState([]);
    const [
        vendorAdvancePayables,
        setVendorAdvancePayables,
    ] = useState([]);

    const [
        loading,
        setLoading,
    ] = useState(true);

    const [
        loadError,
        setLoadError,
    ] = useState("");

    const [
        search,
        setSearch,
    ] = useState("");

    const [
        statusFilter,
        setStatusFilter,
    ] = useState("ALL");

    const [
        selectedPayable,
        setSelectedPayable,
    ] = useState(null);

    const [
        selectedTransportPayable,
        setSelectedTransportPayable,
    ] = useState(null);

    const [
        showTransportViewModal,
        setShowTransportViewModal,
    ] = useState(false);

    const [
        showTransportPaymentModal,
        setShowTransportPaymentModal,
    ] = useState(false);


    const [
        showPaymentModal,
        setShowPaymentModal,
    ] = useState(false);
    const [
    selectedVendorAdvance,
    setSelectedVendorAdvance,
] = useState(null);

    const [
        showHistoryModal,
        setShowHistoryModal,
    ] = useState(false);

    const [
        selectedAdjustmentPayable,
        setSelectedAdjustmentPayable,
    ] = useState(null);

    const [
        adjustableVendorAdvances,
        setAdjustableVendorAdvances,
    ] = useState([]);

    const [
        showAdjustmentModal,
        setShowAdjustmentModal,
    ] = useState(false);

    const [
        adjustmentAmount,
        setAdjustmentAmount,
    ] = useState("");

    const [
        adjustmentDate,
        setAdjustmentDate,
    ] = useState(
        new Date().toISOString().slice(0, 10)
    );

    const [
        adjustmentRemarks,
        setAdjustmentRemarks,
    ] = useState("");

    const [
        selectedAdjustmentAdvance,
        setSelectedAdjustmentAdvance,
    ] = useState(null);


    const [selectedVendorAdvanceView, setSelectedVendorAdvanceView] = useState(null);
    const [showVendorAdvanceViewModal, setShowVendorAdvanceViewModal] = useState(false);
    const [
        paymentAmount,
        setPaymentAmount,
    ] = useState("");

    const [
        paymentAccount,
        setPaymentAccount,
    ] = useState("");

    const [
    paymentAccountLabel,
    setPaymentAccountLabel,
] = useState("");

    const [
        paymentDate,
        setPaymentDate,
    ] = useState(
        new Date().toISOString().slice(0, 10)
    );

    const [
        paymentReference,
        setPaymentReference,
    ] = useState("");

    const [paymentMode, setPaymentMode] = useState("");

    const [
        transportPayables,
        setTransportPayables,
    ] = useState([]);

    // ==============================================================
    // LOAD PAYABLES
    // ==============================================================

    useEffect(() => {

        let mounted = true;

        async function loadPayables() {

            try {

                setLoading(true);

                setLoadError("");

                const rows =
                    await listPayables();

                if (mounted) {

                    setPayables(
                        rows
                    );
                }

                const advanceRows = await listVendorAdvancePayables();
                setVendorAdvancePayables(advanceRows);
                console.log("Vendor Advance Payables:", advanceRows);
                const transportRows = await listTransportPayables();

                    if (mounted) {
                        setTransportPayables(transportRows);
                    }

                    console.log("Transport Payables:", transportRows);

            } catch (error) {

                if (mounted) {

                    setLoadError(
                        error?.response?.data?.message ||
                        error?.message ||
                        "Unable to load payables."
                    );

                    setPayables([]);
                }

            } finally {

                if (mounted) {

                    setLoading(false);
                }
            }
        }

        loadPayables();

        return () => {

            mounted = false;
        };

    }, []);


    // ==============================================================
    // UI DATA MAPPING
    // ==============================================================

    const uiPayables = useMemo(
        () =>
            payables.map(
                (row) => ({
                    ...row,

                    id:
                        row.purchase_invoice_id,

                    vendor_name:
                        row.vendor_name ||
                        `Vendor #${row.vendor_id}`,

                    invoice_no:
                        row.pi_no,

                    invoice_date:
                        row.pi_date,

                    currency:
                        row.currency_code ||
                        "INR",

                    invoice_amount:
                        row.original_amount,

                    paid_amount:
                        row.allocated_amount,

                    status:
                        row.payment_status,
                })
            ),
        [
            payables,
        ]
    );


    // ==============================================================
    // FILTERED DATA
    // ==============================================================

    const filteredPayables =
        useMemo(
            () => {

                const searchText =
                    search
                        .trim()
                        .toLowerCase();

                return uiPayables.filter(
                    (item) => {

                        const matchesSearch =
                            !searchText ||
                            String(
                                item.vendor_name ||
                                ""
                            )
                                .toLowerCase()
                                .includes(
                                    searchText
                                ) ||
                            String(
                                item.invoice_no ||
                                ""
                            )
                                .toLowerCase()
                                .includes(
                                    searchText
                                ) ||
                            String(
                                item.supplier_invoice_no ||
                                ""
                            )
                                .toLowerCase()
                                .includes(
                                    searchText
                                );

                        const matchesStatus =
                            statusFilter === "ALL" ||
                            item.status ===
                                statusFilter;

                        return (
                            matchesSearch &&
                            matchesStatus
                        );
                    }
                );

            },
            [
                uiPayables,
                search,
                statusFilter,
            ]
        );


    // ==============================================================
    // SUMMARY
    // ==============================================================

    const summary =
        useMemo(
            () => {

                const totalOutstanding =
                    uiPayables.reduce(
                        (
                            total,
                            item
                        ) =>
                            total +
                            Number(
                                item.outstanding_amount ||
                                0
                            ),
                        0
                    );

                const totalPaid =
                    uiPayables.reduce(
                        (
                            total,
                            item
                        ) =>
                            total +
                            Number(
                                item.paid_amount ||
                                0
                            ),
                        0
                    );

                const totalInvoices =
                    uiPayables.length;

                const partiallyPaid =
                    uiPayables.filter(
                        (item) =>
                            item.status ===
                            "PARTIALLY_PAID"
                    ).length;

                const unpaid =
                    uiPayables.filter(
                        (item) =>
                            item.status ===
                            "UNPAID"
                    ).length;

                return {
                    totalOutstanding,
                    totalPaid,
                    totalInvoices,
                    partiallyPaid,
                    unpaid,
                };

            },
            [
                uiPayables,
            ]
        );


    // ==============================================================
    // OPEN PAYMENT MODAL
    // ==============================================================

    const handlePay = (
        payable
    ) => {

        setSelectedPayable(
            payable
        );
        setSelectedVendorAdvance(null);
        setPaymentAmount(
            ""
        );

        setPaymentAccount(
            null
        );

        setPaymentDate(
            new Date()
                .toISOString()
                .slice(0, 10)
        );

        setPaymentReference(
            ""
        );
        setPaymentMode(
            ""
        );

        setShowPaymentModal(
            true
        );
    };

    const handleAdjustAdvance = async (
        payable
    ) => {

        try {

            const rows =
                await listAdjustableVendorAdvances(
                    payable.purchase_invoice_id
                );

            setSelectedAdjustmentPayable(
                payable
            );

            setAdjustableVendorAdvances(
                rows
            );

            setSelectedAdjustmentAdvance(
                null
            );

            setAdjustmentAmount(
                ""
            );

            setAdjustmentDate(
                new Date()
                    .toISOString()
                    .slice(0, 10)
            );

            setAdjustmentRemarks(
                ""
            );

            setShowAdjustmentModal(
                true
            );

        } catch (error) {

            alert(
                error?.response?.data?.message ||
                error?.message ||
                "Unable to load Vendor Advances."
            );
        }
    };

    const handleSubmitAdjustment = async () => {

        if (!selectedAdjustmentPayable) {
            return;
        }

        if (!selectedAdjustmentAdvance) {
            alert("Please select a Vendor Advance.");
            return;
        }

        if (!adjustmentAmount || Number(adjustmentAmount) <= 0) {
            alert("Please enter a valid Adjustment Amount.");
            return;
        }

        const amount = Number(adjustmentAmount);

        const availableAdjustmentAmount = Number(
            selectedAdjustmentAdvance.available_adjustment_amount || 0
        );

        const payableOutstanding = Number(
            selectedAdjustmentPayable.outstanding_amount || 0
        );

        const maximumAdjustmentAmount = Math.min(
            availableAdjustmentAmount,
            payableOutstanding
        );

        if (amount > maximumAdjustmentAmount) {
            alert(
                `Adjustment amount cannot exceed ${formatCurrency(
                    maximumAdjustmentAmount,
                    selectedAdjustmentPayable.currency_code
                )}.`
            );
            return;
        }
        
        try {

            await createVendorAdvanceAdjustment(
                selectedAdjustmentAdvance.vendor_advance_id,
                {
                    purchase_invoice_id:
                        selectedAdjustmentPayable.purchase_invoice_id,

                    adjustment_amount:
                        Number(adjustmentAmount),

                    adjustment_date:
                        adjustmentDate,

                    remarks:
                        adjustmentRemarks || null,
                }
            );

            alert(
                "Vendor Advance adjusted successfully."
            );

            setShowAdjustmentModal(false);

            setSelectedAdjustmentPayable(null);

            setSelectedAdjustmentAdvance(null);

            setAdjustmentAmount("");

            setAdjustmentRemarks("");

        } catch (error) {

            alert(
                error?.response?.data?.message ||
                error?.message ||
                "Unable to adjust Vendor Advance."
            );
        }
    };
    const handlePayVendorAdvance = (
    advance
) => {

    setSelectedVendorAdvance(
        advance
    );

    setSelectedPayable(
        null
    );

    setPaymentAmount(
        ""
    );

    setPaymentAccount(
        null
    );

    setPaymentDate(
        new Date()
            .toISOString()
            .slice(0, 10)
    );

    setPaymentReference(
        ""
    );

    setPaymentMode(
        ""
    );

    setShowPaymentModal(
        true
    );
};
const handleViewVendorAdvance = (advance) => {
    setSelectedVendorAdvanceView(advance);
    setShowVendorAdvanceViewModal(true);
};

const handleContinueVendorAdvancePayment = async () => {
    if (!selectedVendorAdvance) {
        return;
    }

    const amount = Number(paymentAmount);

    if (!amount || amount <= 0) {
        alert("Please enter a valid payment amount.");
        return;
    }

    if (amount > Number(selectedVendorAdvance.outstanding_amount || 0)) {
        alert("Payment amount cannot exceed the outstanding advance amount.");
        return;
    }

    if (!paymentAccount) {
        alert("Please select a payment account.");
        return;
    }

    if (!paymentMode) {
        alert("Please select a payment mode.");
        return;
    }

    if (!paymentDate) {
        alert("Please select a payment date.");
        return;
    }

    try {
        const payload = {
            payment_amount: amount,
            payment_account_id: Number(paymentAccount),
            payment_mode: paymentMode,
            payment_date: paymentDate,
            payment_reference: paymentReference || null,
        };

        await createPaymentForVendorAdvance(
            selectedVendorAdvance.vendor_advance_id,
            payload
        );

        setShowPaymentModal(false);
        setSelectedVendorAdvance(null);
        setPaymentAmount("");
        setPaymentAccount("");
        setPaymentAccountLabel("");
        setPaymentReference("");

        alert("Vendor advance payment created successfully.");

        const rows = await listVendorAdvancePayables();
        setVendorAdvancePayables(rows);
    } catch (error) {
        console.error(
            "Vendor Advance payment failed:",
            error
        );

        alert(
            error?.response?.data?.message ||
            error?.message ||
            "Failed to create vendor advance payment."
        );
    }
};


const loadPaymentAccounts = async (inputValue = "") => {
    const companyId =
        selectedPayable?.company_id
        || selectedTransportPayable?.company_id;

    if (!companyId) {
        return [];
    }

    try {
        const response =
            await financeLookupService.searchPaymentAccounts(
                companyId,
                inputValue,
                20
            );

        return (
            response || []
        ).map(account => ({
            ...account,
            value: account.id,
            label:
                `${account.account_name || "Unnamed Account"}`
                + (
                    account.account_number
                        ? ` - ${account.account_number}`
                        : ""
                    )
                + (
                    account.branch_name
                        ? ` (${account.branch_name})`
                        : ""
                    ),
        }));
    } catch (error) {
        console.error("Payment Account lookup failed:", error);
        return [];
    }
};

const loadVendorAdvancePaymentAccounts = async (inputValue = "") => {
    if (!selectedVendorAdvance?.company_id) {
        return [];
    }

    try {
        const response =
            await financeLookupService.searchPaymentAccounts(
                selectedVendorAdvance.company_id,
                inputValue,
                20
            );

        return (
            response || []
        ).map(account => ({
            ...account,
            value: account.id,
            label:
                `${account.account_name || "Unnamed Account"}`
                + (
                    account.account_number
                        ? ` - ${account.account_number}`
                        : ""
                    )
                + (
                    account.branch_name
                        ? ` (${account.branch_name})`
                        : ""
                    ),
        }));
    } catch (error) {
        console.error(
            "Vendor Advance payment account lookup failed:",
            error
        );
        return [];
    }
};

const handleContinuePayment = async () => {

    if (!selectedPayable) {
        return;
    }

    try {

        const payload = {
            payment_amount: paymentAmount,
            payment_account_id: paymentAccount,
            payment_mode: paymentMode,
            payment_date: paymentDate,
            reference_no: paymentReference,
        };

        const voucher =
            await createPaymentForPayable(
                selectedPayable.id,
                payload
            );

        console.log(
            "Payment Voucher created:",
            voucher
        );

        // Close payment modal
        setShowPaymentModal(false);

        // Clear selected payable
        setSelectedPayable(null);

        // Show success message
        alert(
            `Payment Voucher ${voucher?.voucher_no || ""} created successfully.`
        );

        // Refresh payable list
        const rows = await listPayables();

        setPayables(rows);

    } catch (error) {

        console.error(
            "Failed to create payment voucher:",
            error
        );

        alert(
            error?.response?.data?.message ||
            error?.message ||
            "Failed to create Payment Voucher."
        );
    }
};    
const handleContinuePayment_11 = async () => {

    if (!selectedPayable) {
        return;
    }

    try {

        const payload = {
            payment_amount: paymentAmount,
            payment_account_id: paymentAccount,
            payment_mode: paymentMode,
            payment_date: paymentDate,
            reference_no: paymentReference,
        };

        const voucher =
            await createPaymentForPayable(
                selectedPayable.id,
                payload
            );

        console.log(
            "Payment Voucher created:",
            voucher
        );

        // Temporary for testing only.
        // We will replace this with the proper next-step
        // Payment Voucher workflow after confirming the API works.

    } catch (error) {

        console.error(
            "Failed to create payment voucher:",
            error
        );
    }
};

    // ==============================================================
    // OPEN HISTORY
    // ==============================================================

const handleViewHistory = async (payable) => {
    try {
        const detail = await getPayable(payable.id);

        if (detail) {
            setSelectedPayable(detail);
            setShowHistoryModal(true);
        }
    } catch (error) {
        console.error(
            "Failed to load payable details:",
            error
        );
    }
};
    // ==============================================================
    // CLOSE PAYMENT MODAL
    // ==============================================================

    const closePaymentModal = () => {

        setShowPaymentModal(
            false
        );

        setSelectedPayable(
            null
        );
    };


    // ==============================================================
    // CLOSE HISTORY MODAL
    // ==============================================================

    const closeHistoryModal = () => {

        setShowHistoryModal(
            false
        );

        setSelectedPayable(
            null
        );
    };


    // ==============================================================
    // PAYMENT AMOUNT
    // ==============================================================

    
    const outstandingAmount =
    Number(
        selectedVendorAdvance?.outstanding_amount ??
        selectedPayable?.outstanding_amount ??
        0
    );    

    
    
    const handleTransportPay = (item) => {
        setSelectedTransportPayable(item);

        setPaymentAmount("");
        setPaymentAccount(null);
        setPaymentAccountLabel("");
        setPaymentDate(new Date().toISOString().slice(0, 10));
        setPaymentReference("");
        setPaymentMode("");

        setShowTransportPaymentModal(true);
    };
    

const handleTransportPaymentSubmit = async () => {
    if (!selectedTransportPayable) {
        return;
    }

    if (
        !paymentAmount ||
        Number(paymentAmount) <= 0 ||
        !paymentAccount ||
        !paymentMode ||
        !paymentDate
    ) {
        alert("Please complete all required payment fields.");
        return;
    }

    try {
        const payload = {
            payment_amount: Number(paymentAmount),
            payment_account_id: paymentAccount,
            payment_mode: paymentMode,
            payment_date: paymentDate,
            reference_no: paymentReference || null,
        };

        await createPaymentForTransportPayable(
            selectedTransportPayable.transport_vehicle_id,
            payload
        );

        alert(
            "Transport Payment Voucher created successfully as Draft."
        );

        setShowTransportPaymentModal(false);
        setSelectedTransportPayable(null);
    } catch (error) {
        console.error(
            "Failed to create Transport Payment Voucher:",
            error
        );

        alert(
            error?.response?.data?.message
                || error?.response?.data?.error
                || "Failed to create Transport Payment Voucher."
        );
    }
};

    // ==============================================================
    // RENDER
    // ==============================================================

    return (

        <div className="container-fluid py-3">

            {/* ======================================================
                PAGE HEADER
            ====================================================== */}

            <div className="d-flex justify-content-between align-items-center mb-4">

                <div>

                    <h4 className="mb-1">

                        <i className="fas fa-file-invoice-dollar me-2" />

                        Accounts Payable

                    </h4>

                    <div className="text-muted small">

                        Outstanding Purchase Invoice payables

                    </div>

                </div>

            </div>


            {/* ======================================================
                SUMMARY CARDS
            ====================================================== */}

            <div className="row g-3 mb-4">

                <div className="col-md-3">

                    <div className="card shadow-sm border-0 h-100">

                        <div className="card-body">

                            <div className="text-muted small mb-1">

                                Total Payables

                            </div>

                            <div className="fs-4 fw-semibold">

                                {summary.totalInvoices}

                            </div>

                        </div>

                    </div>

                </div>


                <div className="col-md-3">

                    <div className="card shadow-sm border-0 h-100">

                        <div className="card-body">

                            <div className="text-muted small mb-1">

                                Total Outstanding

                            </div>

                            <div className="fs-4 fw-semibold">

                                {formatCurrency(
                                    summary.totalOutstanding
                                )}

                            </div>

                        </div>

                    </div>

                </div>


                <div className="col-md-3">

                    <div className="card shadow-sm border-0 h-100">

                        <div className="card-body">

                            <div className="text-muted small mb-1">

                                Total Paid

                            </div>

                            <div className="fs-4 fw-semibold">

                                {formatCurrency(
                                    summary.totalPaid
                                )}

                            </div>

                        </div>

                    </div>

                </div>


                <div className="col-md-3">

                    <div className="card shadow-sm border-0 h-100">

                        <div className="card-body">

                            <div className="d-flex justify-content-between">

                                <div>

                                    <div className="text-muted small mb-1">

                                        Payment Status

                                    </div>

                                    <div className="small">

                                        <span className="me-3">

                                            Unpaid:
                                            {" "}
                                            <strong>
                                                {summary.unpaid}
                                            </strong>

                                        </span>

                                        <span>

                                            Partial:
                                            {" "}
                                            <strong>
                                                {summary.partiallyPaid}
                                            </strong>

                                        </span>

                                    </div>

                                </div>

                            </div>

                        </div>

                    </div>

                </div>

            </div>


            {/* ======================================================
                FILTERS
            ====================================================== */}

            <div className="card shadow-sm border-0 mb-4">

                <div className="card-body">

                    <div className="row g-3 align-items-end">

                        <div className="col-md-6">

                            <label className="form-label">

                                Search

                            </label>

                            <div className="input-group">

                                <span className="input-group-text">

                                    <i className="fas fa-search" />

                                </span>

                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Search vendor or invoice..."
                                    value={search}
                                    onChange={(e) =>
                                        setSearch(
                                            e.target.value
                                        )
                                    }
                                />

                            </div>

                        </div>


                        <div className="col-md-3">

                            <label className="form-label">

                                Payment Status

                            </label>

                            <select
                                className="form-select"
                                value={statusFilter}
                                onChange={(e) =>
                                    setStatusFilter(
                                        e.target.value
                                    )
                                }
                            >

                                <option value="ALL">

                                    All

                                </option>

                                <option value="UNPAID">

                                    Unpaid

                                </option>

                                <option value="PARTIALLY_PAID">

                                    Partially Paid

                                </option>

                            </select>

                        </div>


                        <div className="col-md-3 text-md-end">

                            <div className="text-muted small">

                                Showing
                                {" "}
                                <strong>
                                    {filteredPayables.length}
                                </strong>
                                {" "}
                                payable(s)

                            </div>

                        </div>

                    </div>

                </div>

            </div>


            {/* ======================================================
                PAYABLE GRID
            ====================================================== */}

            {loadError && (

                <div className="alert alert-danger mb-4">

                    <i className="fas fa-exclamation-circle me-2" />

                    {loadError}

                </div>

            )}

            {loading && (

                <div className="alert alert-info mb-4">

                    <i className="fas fa-spinner fa-spin me-2" />

                    Loading payables...

                </div>

            )}

            <div className="card shadow-sm border-0">

                <div className="card-header bg-white">

                    <div className="fw-semibold">

                        Outstanding Purchase Invoices and Payments

                    </div>

                </div>

                <div className="card-body p-0">

                    <div className="table-responsive">

                        <table className="table table-hover align-middle mb-0">

                            <thead className="table-light">

                                <tr>

                                    <th>
                                        Vendor
                                    </th>

                                    <th>
                                        Purchase Invoice
                                    </th>

                                    <th>
                                        Invoice Date
                                    </th>

                                    <th className="text-end">
                                        Invoice Amount
                                    </th>

                                    <th className="text-end">
                                        Paid
                                    </th>

                                    <th className="text-end">
                                        Outstanding
                                    </th>

                                    <th>
                                        Status
                                    </th>

                                    <th className="text-end">
                                        Action
                                    </th>

                                </tr>

                            </thead>

                            <tbody>

                                {filteredPayables.length === 0 && !loading ? (

                                    <tr>

                                        <td
                                            colSpan="8"
                                            className="text-center py-5 text-muted"
                                        >

                                            <i className="fas fa-inbox fa-2x mb-2 d-block" />

                                            No outstanding payables found.

                                        </td>

                                    </tr>

                                ) : (

                                    filteredPayables.map(
                                        (
                                            payable
                                        ) => (

                                            <tr
                                                key={
                                                    payable.id
                                                }
                                            >

                                                <td>

                                                    <div className="fw-semibold">

                                                        {
                                                            payable.vendor_name
                                                        }

                                                    </div>

                                                    <div className="small text-muted">

                                                        Vendor ID:
                                                        {" "}
                                                        {
                                                            payable.vendor_id
                                                        }

                                                    </div>

                                                </td>

                                                <td>

                                                    <div className="fw-semibold">

                                                        {
                                                            payable.invoice_no
                                                        }

                                                    </div>

                                                    {payable.supplier_invoice_no && (

                                                        <div className="small text-muted">

                                                            Supplier:
                                                            {" "}
                                                            {
                                                                payable.supplier_invoice_no
                                                            }

                                                        </div>

                                                    )}

                                                </td>

                                                <td>

                                                    {
                                                        formatDate(
                                                            payable.invoice_date
                                                        )
                                                    }

                                                </td>

                                                <td className="text-end">

                                                    {
                                                        formatCurrency(
                                                            payable.invoice_amount,
                                                            payable.currency_code
                                                        )
                                                    }

                                                </td>

                                                <td className="text-end">

                                                    {
                                                        formatCurrency(
                                                            payable.paid_amount,
                                                            payable.currency_code
                                                        )
                                                    }

                                                </td>

                                                <td className="text-end fw-semibold">

                                                    {
                                                        formatCurrency(
                                                            payable.outstanding_amount,
                                                            payable.currency_code
                                                        )
                                                    }

                                                </td>

                                                <td>

                                                    <span
                                                        className={
                                                            `badge ${getStatusClass(
                                                                payable.status
                                                            )}`
                                                        }
                                                    >

                                                        {
                                                            payable.status
                                                                ?.replace(
                                                                    "_",
                                                                    " "
                                                                )
                                                        }

                                                    </span>

                                                </td>

                                                <td className="text-end">

                                                    <div className="btn-group">

                                                        <button
                                                            type="button"
                                                            className="btn btn-sm btn-outline-secondary"
                                                            onClick={() =>
                                                                handleViewHistory(
                                                                    payable
                                                                )
                                                            }
                                                        >

                                                            <i className="fas fa-eye me-1" />

                                                            View

                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm btn-outline-warning ms-1"
                                                            onClick={() =>
                                                                handleAdjustAdvance(payable)
                                                            }
                                                        >
                                                            Adjust Advance
                                                        </button>

                                                        <button
                                                            type="button"
                                                            className="btn btn-sm btn-primary"
                                                            onClick={() =>
                                                                handlePay(
                                                                    payable
                                                                )
                                                            }
                                                        >

                                                            <i className="fas fa-money-bill-wave me-1" />

                                                            Pay

                                                        </button>

                                                    </div>

                                                </td>

                                            </tr>

                                        )
                                    )

                                )}

                            </tbody>

                        </table>

                    </div>

                </div>

                <div className="card-footer bg-white">

                    <div className="text-muted small">

                        Finance Payables

                    </div>

                </div>

            </div>

            {/* ======================================================
                VENDOR ADVANCES
            ====================================================== */}

            <div className="card shadow-sm border-0 mt-4">

                <div className="card-header bg-white">

                    <div className="d-flex justify-content-between align-items-center">

                        <div className="fw-semibold">

                            Outstanding Vendor Advances

                        </div>

                        <div className="text-muted small">

                            Showing {vendorAdvancePayables.length} advance(s)

                        </div>

                    </div>

                </div>

                <div className="card-body p-0">

                    <div className="table-responsive">

                        <table className="table table-hover align-middle mb-0">

                            <thead className="table-light">

                                <tr>

                                    <th>
                                        Vendor
                                    </th>

                                    <th>
                                        Purchase Order
                                    </th>

                                    <th>
                                        Advance Type
                                    </th>

                                    <th className="text-end">
                                        Advance %
                                    </th>

                                    <th className="text-end">
                                        Advance Amount
                                    </th>

                                    <th className="text-end">
                                        Paid Amount
                                    </th>

                                    <th className="text-end">
                                        Adjusted Amount
                                    </th>

                                    <th className="text-end">
                                        Outstanding Amount
                                    </th>

                                    <th>
                                        Status
                                    </th>

                                    <th className="text-end">
                                        Action
                                    </th>

                                </tr>

                            </thead>

                            <tbody>

                                {vendorAdvancePayables.length === 0 ? (

                                    <tr>

                                        <td
                                            colSpan="10"
                                            className="text-center py-5 text-muted"
                                        >

                                            <i className="fas fa-inbox fa-2x mb-2 d-block" />

                                            No outstanding vendor advances found.

                                        </td>

                                    </tr>

                                ) : (

                                    vendorAdvancePayables.map(
                                        (advance) => (

                                            <tr
                                                key={
                                                    advance.vendor_advance_id
                                                }
                                            >

                                                <td>

                                                    <div className="fw-semibold">

                                                        {advance.vendor_name || `Vendor #${advance.vendor_id}`}

                                                    </div>

                                                    <div className="small text-muted">

                                                        Vendor ID:
                                                        {" "}
                                                        {advance.vendor_id}

                                                    </div>

                                                </td>

                                                <td>

                                                    <div className="fw-semibold">

                                                        {advance.po_no || `PO #${advance.purchase_order_id}`}

                                                    </div>

                                                    <div className="small text-muted">

                                                        PO ID:
                                                        {" "}
                                                        {advance.purchase_order_id}

                                                    </div>

                                                </td>
                                                <td>

                                                    {advance.advance_type}

                                                </td>

                                                <td className="text-end">

                                                    {
                                                        advance.advance_percentage !== null &&
                                                        advance.advance_percentage !== undefined
                                                            ? `${advance.advance_percentage}%`
                                                            : "-"
                                                    }

                                                </td>

                                                <td className="text-end fw-semibold">

                                                    {formatCurrency(
                                                        advance.advance_amount,
                                                        advance.currency_code || "INR"
                                                    )}

                                                </td>

                                                <td className="text-end">

                                                    {formatCurrency(
                                                        advance.paid_amount,
                                                        advance.currency_code || "INR"
                                                    )}

                                                </td>

                                                <td className="text-end">

                                                    {formatCurrency(
                                                        advance.adjusted_amount,
                                                        advance.currency_code || "INR"
                                                    )}

                                                </td>

                                                <td className="text-end fw-semibold">

                                                    {formatCurrency(
                                                        advance.outstanding_amount,
                                                        advance.currency_code || "INR"
                                                    )}

                                                </td>

                                                <td>

                                                    <span className="badge bg-warning text-dark">

                                                        {
                                                            advance.status
                                                                ?.replace(
                                                                    "_",
                                                                    " "
                                                                )
                                                        }

                                                    </span>

                                                </td>

                                                <td className="text-end">

                                                    <button
                                                        type="button"
                                                        className="btn btn-sm btn-outline-secondary me-1"
                                                        onClick={() =>
                                                            handleViewVendorAdvance(advance)
                                                        }
                                                    >
                                                        <i className="fas fa-eye me-1" />
                                                        View
                                                    </button>

                                                    <button
                                                        type="button"
                                                        className="btn btn-sm btn-primary"
                                                        onClick={() =>
                                                            handlePayVendorAdvance(advance)
                                                        }
                                                    >
                                                        <i className="fas fa-money-bill-wave me-1" />
                                                        Pay
                                                    </button>

                                                </td>

                                            </tr>

                                        )
                                    )

                                )}

                            </tbody>

                        </table>

                    </div>

                </div>

            </div>                    
            

            {/* ======================================================
                TRANSPORT PAYABLES
            ====================================================== */}

            <div className="card shadow-sm border-0 mt-4">

                <div className="card-header bg-white">
                    <div className="d-flex justify-content-between align-items-center">
                        <div className="fw-semibold">
                            Outstanding Transport Payables
                        </div>

                        <div className="text-muted small">
                            Showing {transportPayables.length} transport request(s)
                        </div>
                    </div>
                </div>

                <div className="card-body p-0">
                    <div className="table-responsive">
                        <table className="table table-hover align-middle mb-0">

                            <thead className="table-light">
                                <tr>
                                    <th>Transport Request</th>
                                    <th>Vendor</th>
                                    <th className="text-end">Total Amount</th>
                                    <th className="text-end">Finance Paid</th>
                                    <th className="text-end">Outstanding</th>
                                    <th>Status</th>
                                    <th className="text-end">Actions</th>
                                </tr>
                            </thead>

                            <tbody>
                                {transportPayables.length === 0 ? (
                                    <tr>
                                        <td
                                            colSpan="7"
                                            className="text-center py-5 text-muted"
                                        >
                                            <i className="fas fa-inbox fa-2x mb-2 d-block" />
                                            No outstanding transport payables found.
                                        </td>
                                    </tr>
                                ) : (
                                    transportPayables.map((item) => (
                                        <tr key={item.transport_request_id}>
                                            <td>
                                                <div className="fw-semibold">
                                                    {item.transport_code || `TR-${item.transport_request_id}`}
                                                </div>
                                                <div className="small text-muted">
                                                    Request ID: {item.transport_request_id}
                                                </div>
                                            </td>

                                            <td>
                                                <div className="fw-semibold">
                                                    {item.vendor_name || `Vendor #${item.vendor_id}`}
                                                </div>
                                                <div className="small text-muted">
                                                    Vendor ID: {item.vendor_id}
                                                </div>
                                            </td>

                                            <td className="text-end">
                                                {formatCurrency(item.original_amount, "INR")}
                                            </td>

                                            <td className="text-end">
                                                {formatCurrency(item.allocated_amount, "INR")}
                                            </td>

                                            <td className="text-end fw-semibold">
                                                {formatCurrency(item.outstanding_amount, "INR")}
                                            </td>

                                            <td>
                                                <span
                                                    className={`badge ${getStatusClass(
                                                        item.payment_status
                                                    )}`}
                                                >
                                                    {item.payment_status?.replaceAll("_", " ") || "-"}
                                                </span>
                                            </td>
                                            <td className="text-end">
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-outline-secondary me-2"
                                                onClick={() => {
                                                        setSelectedTransportPayable(item);
                                                        setShowTransportViewModal(true);
                                                    }}
                                            >
                                                <i className="fas fa-eye me-1" />
                                                View
                                            </button>

                                            {Number(item.outstanding_amount || 0) > 0 && (
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-primary"
                                                    onClick={() => handleTransportPay(item)}
                                                >
                                                    <i className="fas fa-money-bill-wave me-1" />
                                                    Pay
                                                </button>
                                            )}
                                        </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>

                        </table>
                    </div>
                </div>

                <div className="card-footer bg-white">
                    <div className="text-muted small">
                        Finance Payment Vouchers only — operational Transport payments
                        do not reduce Finance outstanding until posted.
                    </div>
                </div>

            </div>
            
            
            
            {/* ======================================================
                TRANSPORT PAYABLE VIEW MODAL
            ====================================================== */}

            {showTransportViewModal && selectedTransportPayable && (
                <div
                    className="modal fade show d-block"
                    tabIndex="-1"
                    role="dialog"
                    aria-modal="true"
                    style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
                >
                    <div className="modal-dialog modal-lg modal-dialog-centered">
                        <div className="modal-content">

                            <div className="modal-header">
                                <h5 className="modal-title">
                                    Transport Payable Details
                                </h5>

                                <button
                                    type="button"
                                    className="btn-close"
                                    aria-label="Close"
                                    onClick={() => {
                                        setShowTransportViewModal(false);
                                        setSelectedTransportPayable(null);
                                    }}
                                />
                            </div>

                            <div className="modal-body">
                                <div className="row g-3">

                                    <div className="col-md-6">
                                        <label className="form-label text-muted">
                                            Transport Request
                                        </label>
                                        <div className="fw-semibold">
                                            {selectedTransportPayable.transport_code ||
                                                `TR-${selectedTransportPayable.transport_request_id}`}
                                        </div>
                                    </div>

                                    <div className="col-md-6">
                                        <label className="form-label text-muted">
                                            Request ID
                                        </label>
                                        <div className="fw-semibold">
                                            {selectedTransportPayable.transport_request_id}
                                        </div>
                                    </div>

                                    <div className="col-md-6">
                                        <label className="form-label text-muted">
                                            Vendor
                                        </label>
                                        <div className="fw-semibold">
                                            {selectedTransportPayable.vendor_name ||
                                                `Vendor #${selectedTransportPayable.vendor_id}`}
                                        </div>
                                        <small className="text-muted">
                                            Vendor ID: {selectedTransportPayable.vendor_id}
                                        </small>
                                    </div>

                                    <div className="col-md-6">
                                        <label className="form-label text-muted">
                                            Payment Status
                                        </label>
                                        <div>
                                            <span
                                                className={`badge ${getStatusClass(
                                                    selectedTransportPayable.payment_status
                                                )}`}
                                            >
                                                {selectedTransportPayable.payment_status
                                                    ?.replaceAll("_", " ") || "-"}
                                            </span>
                                        </div>
                                    </div>

                                    <div className="col-md-4">
                                        <label className="form-label text-muted">
                                            Total Amount
                                        </label>
                                        <div className="fw-semibold">
                                            {formatCurrency(
                                                selectedTransportPayable.original_amount,
                                                "INR"
                                            )}
                                        </div>
                                    </div>

                                    <div className="col-md-4">
                                        <label className="form-label text-muted">
                                            Finance Paid
                                        </label>
                                        <div className="fw-semibold text-success">
                                            {formatCurrency(
                                                selectedTransportPayable.allocated_amount,
                                                "INR"
                                            )}
                                        </div>
                                    </div>

                                    <div className="col-md-4">
                                        <label className="form-label text-muted">
                                            Outstanding Amount
                                        </label>
                                        <div className="fw-semibold text-danger">
                                            {formatCurrency(
                                                selectedTransportPayable.outstanding_amount,
                                                "INR"
                                            )}
                                        </div>
                                    </div>

                                </div>
                            </div>

                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={() => {
                                        setShowTransportViewModal(false);
                                        setSelectedTransportPayable(null);
                                    }}
                                >
                                    Close
                                </button>
                            </div>

                        </div>
                    </div>
                </div>
            )}

            
            {/* ======================================================
                TRANSPORT PAYMENT MODAL
            ====================================================== */}

            {showTransportPaymentModal && selectedTransportPayable && (
                <div
                    className="modal fade show d-block"
                    tabIndex="-1"
                    role="dialog"
                    aria-modal="true"
                    style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
                >
                    <div className="modal-dialog modal-lg modal-dialog-centered">
                        <div className="modal-content">

                            <div className="modal-header">
                                <h5 className="modal-title">
                                    <i className="fas fa-truck me-2" />
                                    Transport Payment
                                </h5>

                                <button
                                    type="button"
                                    className="btn-close"
                                    aria-label="Close"
                                    onClick={() => {
                                        setShowTransportPaymentModal(false);
                                    }}
                                />
                            </div>

                            <div className="modal-body">
                                <div className="row g-3 mb-4">

                                    <div className="col-md-6">
                                        <label className="form-label text-muted">
                                            Transport Request
                                        </label>
                                        <div className="fw-semibold">
                                            {selectedTransportPayable.transport_code ||
                                                `TR-${selectedTransportPayable.transport_request_id}`}
                                        </div>
                                    </div>

                                    <div className="col-md-6">
                                        <label className="form-label text-muted">
                                            Vendor
                                        </label>
                                        <div className="fw-semibold">
                                            {selectedTransportPayable.vendor_name ||
                                                `Vendor #${selectedTransportPayable.vendor_id}`}
                                        </div>
                                    </div>

                                    <div className="col-md-4">
                                        <label className="form-label text-muted">
                                            Total Transport Amount
                                        </label>
                                        <div className="fw-semibold">
                                            {formatCurrency(
                                                selectedTransportPayable.original_amount,
                                                "INR"
                                            )}
                                        </div>
                                    </div>

                                    <div className="col-md-4">
                                        <label className="form-label text-muted">
                                            Finance Paid
                                        </label>
                                        <div className="fw-semibold text-success">
                                            {formatCurrency(
                                                selectedTransportPayable.allocated_amount,
                                                "INR"
                                            )}
                                        </div>
                                    </div>

                                    <div className="col-md-4">
                                        <label className="form-label text-muted">
                                            Outstanding
                                        </label>
                                        <div className="fw-semibold text-danger">
                                            {formatCurrency(
                                                selectedTransportPayable.outstanding_amount,
                                                "INR"
                                            )}
                                        </div>
                                    </div>

                                </div>

                                <hr />

                                <div className="row g-3">

                                    <div className="col-md-6">
                                        <label className="form-label">
                                            Payment Amount *
                                        </label>
                                        <input
                                            type="number"
                                            min="0.01"
                                            max={Number(
                                                selectedTransportPayable.outstanding_amount || 0
                                            )}
                                            step="0.01"
                                            className="form-control"
                                            value={paymentAmount}
                                            onChange={(e) =>
                                                setPaymentAmount(e.target.value)
                                            }
                                            placeholder="Enter payment amount"
                                        />
                                    </div>

                                    <div className="col-md-6">
                                        <label className="form-label">
                                            Payment Account *
                                        </label>
                                        <AsyncSelect
                                            cacheOptions
                                            defaultOptions
                                            loadOptions={loadPaymentAccounts}
                                            value={
                                                paymentAccount
                                                    ? {
                                                        value: paymentAccount,
                                                        label: paymentAccountLabel,
                                                    }
                                                    : null
                                            }
                                            onChange={(selected) => {
                                                setPaymentAccount(
                                                    selected ? selected.value : ""
                                                );
                                                setPaymentAccountLabel(
                                                    selected ? selected.label : ""
                                                );
                                            }}
                                            placeholder="Select bank / cash account"
                                            isClearable
                                        />
                                    </div>

                                    <div className="col-md-6">
                                        <label className="form-label">
                                            Payment Mode *
                                        </label>
                                        <select
                                            className="form-select"
                                            value={paymentMode}
                                            onChange={(e) =>
                                                setPaymentMode(e.target.value)
                                            }
                                        >
                                            <option value="">Select Payment Mode</option>
                                            <option value="BANK_TRANSFER">Bank Transfer</option>
                                            <option value="NEFT">NEFT</option>
                                            <option value="RTGS">RTGS</option>
                                            <option value="IMPS">IMPS</option>
                                            <option value="CHEQUE">Cheque</option>
                                            <option value="CASH">Cash</option>
                                            <option value="OTHER">Other</option>
                                        </select>
                                    </div>

                                    <div className="col-md-6">
                                        <label className="form-label">
                                            Payment Date *
                                        </label>
                                        <input
                                            type="date"
                                            className="form-control"
                                            value={paymentDate}
                                            onChange={(e) =>
                                                setPaymentDate(e.target.value)
                                            }
                                        />
                                    </div>

                                    <div className="col-md-6">
                                        <label className="form-label">
                                            Reference Number
                                        </label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={paymentReference}
                                            onChange={(e) =>
                                                setPaymentReference(e.target.value)
                                            }
                                            placeholder="Cheque / transaction reference"
                                        />
                                    </div>

                                </div>

                                <div className="alert alert-info mt-4 mb-0">
                                    <i className="fas fa-info-circle me-2" />
                                    Transport payments will be handled separately from
                                    Purchase Invoice and Vendor Advance payments.
                                </div>
                            </div>

                            
                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={() => {
                                        setShowTransportPaymentModal(false);
                                    }}
                                >
                                    Cancel
                                </button>

                                <button
                                    type="button"
                                    className="btn btn-primary"
                                    onClick={handleTransportPaymentSubmit}
                                >
                                    Create Payment Voucher
                                </button>
                            </div>


                        </div>
                    </div>
                </div>
            )}


            {/* ======================================================
                PAYMENT MODAL
            ====================================================== */}

            {/*{showPaymentModal && selectedPayable && (*/}
            {/*{showPaymentModal && (selectedPayable || selectedVendorAdvance) && (*/}
            {/* {showPaymentModal && selectedPayable && (  */}    
            {showPaymentModal && (selectedPayable || selectedTransportPayable) && (    
                <div
                    className="modal fade show d-block"
                    tabIndex="-1"
                    style={{
                        backgroundColor:
                            "rgba(0,0,0,0.5)",
                    }}
                >

                    <div className="modal-dialog modal-lg modal-dialog-centered">

                        <div className="modal-content">

                            <div className="modal-header">

                                <h5 className="modal-title">

                                    <i className="fas fa-money-bill-wave me-2" />

                                    Make Payment

                                </h5>

                                <button
                                    type="button"
                                    className="btn-close"
                                    onClick={
                                        closePaymentModal
                                    }
                                />

                            </div>

                            <div className="modal-body">

                                {/* ==================================================
                                    INVOICE SUMMARY
                                ================================================== */}

                                <div className="row g-3 mb-4">

                                    <div className="col-md-6">

                                        <div className="border rounded p-3 h-100">

                                            <div className="text-muted small">

                                                Vendor

                                            </div>

                                            <div className="fw-semibold">

                                                {
                                                    //selectedPayable.vendor_name
                                                    selectedPayable?.vendor_name ||
                                                        selectedVendorAdvance?.vendor_name ||
                                                        ""
                                                }

                                            </div>

                                        </div>

                                    </div>

                                    <div className="col-md-6">

                                        <div className="border rounded p-3 h-100">

                                            <div className="text-muted small">

                                                Purchase Invoice

                                            </div>

                                            <div className="fw-semibold">

                                                {
                                                    //selectedPayable.invoice_no
                                                    selectedPayable?.invoice_no ||
                                                    "Vendor Advance"
                                                }

                                            </div>

                                        </div>

                                    </div>

                                </div>


                                <div className="row g-3 mb-4">

                                    <div className="col-md-4">

                                        <div className="text-muted small">

                                            Invoice Amount

                                        </div>

                                        <div className="fw-semibold">

                                            {
                                                formatCurrency(
                                                    selectedPayable?.invoice_amount || 0,
                                                    selectedPayable?.currency ||
                                                        selectedVendorAdvance?.currency_code ||
                                                        ""
                                                )
                                            }

                                        </div>

                                    </div>

                                    <div className="col-md-4">

                                        <div className="text-muted small">

                                            Already Paid

                                        </div>

                                        <div className="fw-semibold">

                                            {
                                                formatCurrency(
                                                    selectedPayable?.paid_amount || 0,
                                                    selectedPayable?.currency ||
                                                    selectedVendorAdvance?.currency_code ||
                                                    ""
                                                )
                                            }

                                        </div>

                                    </div>

                                    <div className="col-md-4">

                                        <div className="text-muted small">

                                            Outstanding

                                        </div>

                                        <div className="fw-semibold text-danger">

                                            {
                                                formatCurrency(
                                                    outstandingAmount,
                                                    selectedPayable?.currency ||
                                                    selectedVendorAdvance?.currency_code ||
                                                    ""
                                                )
                                            }

                                        </div>

                                    </div>

                                </div>


                                <hr />


                                {/* ==================================================
                                    PAYMENT FORM
                                ================================================== */}

                                <div className="row g-3">

                                    <div className="col-md-6">

                                        <label className="form-label">

                                            Payment Amount
                                            <span className="text-danger">
                                                {" "}*
                                            </span>

                                        </label>

                                        <div className="input-group">

                                            <span className="input-group-text">

                                                {
                                                    //selectedPayable.currency ||
                                                    //"INR"
                                                    selectedPayable?.currency ||
                                                    selectedVendorAdvance?.currency_code ||
                                                    ""
                                                }

                                            </span>

                                            <input
                                                type="number"
                                                min="0"
                                                max={
                                                    outstandingAmount
                                                }
                                                step="0.01"
                                                className="form-control"
                                                value={
                                                    paymentAmount
                                                }
                                                onChange={(e) =>
                                                    setPaymentAmount(
                                                        e.target.value
                                                    )
                                                }
                                                placeholder="Enter payment amount"
                                            />

                                        </div>

                                        <div className="form-text">

                                            Maximum:
                                            {" "}
                                            {
                                                formatCurrency(
                                                    outstandingAmount,
                                                    selectedPayable?.currency ||
                                                    selectedVendorAdvance?.currency_code ||
                                                    ""
                                                )
                                            }

                                        </div>

                                    </div>


                                    <div className="col-md-6">

                                        <label className="form-label">
                                            Payment Account
                                            <span className="text-danger">
                                                {" "}*
                                            </span>
                                        </label>

                                        <AsyncSelect
                        cacheOptions
                        defaultOptions
                        loadOptions={
                            loadPaymentAccounts
                        }
                        value={
                            paymentAccount
                                ? {
                                    value: paymentAccount,
                                    label:
                                        paymentAccountLabel,
                                }
                                : null
                        }
                        onChange={
                            (selected) => {
                                setPaymentAccount(
                                    selected
                                        ? selected.value
                                        : ""
                                );

                                setPaymentAccountLabel(
                                    selected
                                        ? selected.label
                                        : ""
                                );
                            }
                        }
                        placeholder="Select bank / cash account"
                        isClearable
                    />

                                    </div>
                                    
                                    <div className="col-md-6">
                                        <label className="form-label">
                                            Payment Mode
                                        </label>

                                        <select
                                            className="form-select"
                                            value={paymentMode}
                                            onChange={(e) =>
                                                setPaymentMode(e.target.value)
                                            }
                                        >
                                            <option value="">
                                                Select Payment Mode
                                            </option>

                                            <option value="BANK_TRANSFER">
                                                Bank Transfer
                                            </option>

                                            <option value="NEFT">
                                                NEFT
                                            </option>

                                            <option value="RTGS">
                                                RTGS
                                            </option>

                                            <option value="IMPS">
                                                IMPS
                                            </option>

                                            <option value="CHEQUE">
                                                Cheque
                                            </option>

                                            <option value="CASH">
                                                Cash
                                            </option>

                                            <option value="OTHER">
                                                Other
                                            </option>
                                        </select>
                                    </div>



                                    <div className="col-md-6">

                                        <label className="form-label">

                                            Payment Date

                                        </label>

                                        <input
                                            type="date"
                                            className="form-control"
                                            value={
                                                paymentDate
                                            }
                                            onChange={(e) =>
                                                setPaymentDate(
                                                    e.target.value
                                                )
                                            }
                                        />

                                    </div>


                                    <div className="col-md-6">

                                        <label className="form-label">

                                            Reference No.

                                        </label>

                                        <input
                                            type="text"
                                            className="form-control"
                                            value={
                                                paymentReference
                                            }
                                            onChange={(e) =>
                                                setPaymentReference(
                                                    e.target.value
                                                )
                                            }
                                            placeholder="Cheque / transaction reference"
                                        />

                                    </div>

                                </div>


                                {/* ==================================================
                                    ACCOUNTING NOTE
                                ================================================== */}

                                <div className="alert alert-info mt-4 mb-0">

                                    <i className="fas fa-info-circle me-2" />

                                    Payment settlement will create the Finance
                                    payment transaction and reduce the
                                    Purchase Invoice outstanding balance.

                                </div>

                            </div>


                            <div className="modal-footer">

                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={
                                        closePaymentModal
                                    }
                                >

                                    Cancel

                                </button>

                                <button
                                    type="button"
                                    className="btn btn-primary"
                                    disabled={
                                        !paymentAmount ||
                                        Number(
                                            paymentAmount
                                        ) <= 0 ||
                                        Number(
                                            paymentAmount
                                        ) > outstandingAmount
                                    }
                                    //onClick={() => console.log("CONTINUE PAYMENT CLICKED")}
                                    onClick={
                                            handleContinuePayment
                                        }
                                >

                                    <i className="fas fa-check me-1" />

                                    Continue Payment

                                </button>

                            </div>

                        </div>

                    </div>

                </div>

            )}


            {/* ======================================================
                NEW MODAL FOR ADVANCE PAYMENT
            ====================================================== */}
            {showPaymentModal && selectedVendorAdvance && (
                <div
                    className="modal fade show d-block"
                    tabIndex="-1"
                    style={{
                        backgroundColor: "rgba(0,0,0,0.5)",
                    }}
                >
                    <div className="modal-dialog modal-lg modal-dialog-centered">
                        <div className="modal-content">

                            <div className="modal-header">
                                <h5 className="modal-title">
                                    <i className="fas fa-money-bill-wave me-2" />
                                    Vendor Advance Payment
                                </h5>

                                <button
                                    type="button"
                                    className="btn-close"
                                    onClick={closePaymentModal}
                                />
                            </div>

                            <div className="modal-body">

                                {/* ==================================================
                                    ADVANCE SUMMARY
                                ================================================== */}

                                <div className="row g-3 mb-4">

                                    <div className="col-md-6">
                                        <div className="border rounded p-3 h-100">
                                            <div className="text-muted small">
                                                Vendor
                                            </div>

                                            <div className="fw-semibold">
                                                {selectedVendorAdvance.vendor_name || "-"}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="col-md-6">
                                        <div className="border rounded p-3 h-100">
                                            <div className="text-muted small">
                                                Purchase Order
                                            </div>

                                            <div className="fw-semibold">
                                                {selectedVendorAdvance.po_no || "-"}
                                            </div>
                                        </div>
                                    </div>

                                </div>

                                <div className="row g-3 mb-4">

                                    <div className="col-md-4">
                                        <div className="text-muted small">
                                            Advance Amount
                                        </div>

                                        <div className="fw-semibold">
                                            {formatCurrency(
                                                selectedVendorAdvance.advance_amount || 0,
                                                selectedVendorAdvance.currency_code || ""
                                            )}
                                        </div>
                                    </div>

                                    <div className="col-md-4">
                                        <div className="text-muted small">
                                            Already Paid
                                        </div>

                                        <div className="fw-semibold">
                                            {formatCurrency(
                                                selectedVendorAdvance.paid_amount || 0,
                                                selectedVendorAdvance.currency_code || ""
                                            )}
                                        </div>
                                    </div>

                                    <div className="col-md-4">
                                        <div className="text-muted small">
                                            Outstanding
                                        </div>

                                        <div className="fw-semibold text-danger">
                                            {formatCurrency(
                                                selectedVendorAdvance.outstanding_amount || 0,
                                                selectedVendorAdvance.currency_code || ""
                                            )}
                                        </div>
                                    </div>

                                </div>

                                <hr />

                                {/* ==================================================
                                    PAYMENT FORM
                                ================================================== */}

                                <div className="row g-3">

                                    <div className="col-md-6">
                                        <label className="form-label">
                                            Payment Amount
                                            <span className="text-danger"> *</span>
                                        </label>

                                        <div className="input-group">
                                            <span className="input-group-text">
                                                {selectedVendorAdvance.currency_code || ""}
                                            </span>

                                            <input
                                                type="number"
                                                min="0"
                                                max={
                                                    selectedVendorAdvance.outstanding_amount || 0
                                                }
                                                step="0.01"
                                                className="form-control"
                                                value={paymentAmount}
                                                onChange={(e) =>
                                                    setPaymentAmount(e.target.value)
                                                }
                                                placeholder="Enter payment amount"
                                            />
                                        </div>

                                        <div className="form-text">
                                            Maximum:{" "}
                                            {formatCurrency(
                                                selectedVendorAdvance.outstanding_amount || 0,
                                                selectedVendorAdvance.currency_code || ""
                                            )}
                                        </div>
                                    </div>

                                    <div className="col-md-6">
                                        <label className="form-label">
                                            Payment Account
                                            <span className="text-danger"> *</span>
                                        </label>

                                        <AsyncSelect
                                            cacheOptions
                                            defaultOptions
                                            loadOptions={
                                                loadVendorAdvancePaymentAccounts
                                            }
                                            value={
                                                paymentAccount
                                                    ? {
                                                        value: paymentAccount,
                                                        label: paymentAccountLabel,
                                                    }
                                                    : null
                                            }
                                            onChange={(selected) => {
                                                setPaymentAccount(
                                                    selected
                                                        ? selected.value
                                                        : ""
                                                );

                                                setPaymentAccountLabel(
                                                    selected
                                                        ? selected.label
                                                        : ""
                                                );
                                            }}
                                            placeholder="Select bank / cash account"
                                            isClearable
                                        />
                                    </div>

                                    <div className="col-md-6">
                                        <label className="form-label">
                                            Payment Mode
                                        </label>

                                        <select
                                            className="form-select"
                                            value={paymentMode}
                                            onChange={(e) =>
                                                setPaymentMode(e.target.value)
                                            }
                                        >
                                            <option value="">
                                                Select Payment Mode
                                            </option>

                                            <option value="BANK_TRANSFER">
                                                Bank Transfer
                                            </option>

                                            <option value="NEFT">
                                                NEFT
                                            </option>

                                            <option value="RTGS">
                                                RTGS
                                            </option>

                                            <option value="IMPS">
                                                IMPS
                                            </option>

                                            <option value="CHEQUE">
                                                Cheque
                                            </option>

                                            <option value="CASH">
                                                Cash
                                            </option>

                                            <option value="OTHER">
                                                Other
                                            </option>
                                        </select>
                                    </div>

                                    <div className="col-md-6">
                                        <label className="form-label">
                                            Payment Date
                                        </label>

                                        <input
                                            type="date"
                                            className="form-control"
                                            value={paymentDate}
                                            onChange={(e) =>
                                                setPaymentDate(e.target.value)
                                            }
                                        />
                                    </div>

                                    <div className="col-md-6">
                                        <label className="form-label">
                                            Reference No.
                                        </label>

                                        <input
                                            type="text"
                                            className="form-control"
                                            value={paymentReference}
                                            onChange={(e) =>
                                                setPaymentReference(e.target.value)
                                            }
                                            placeholder="Cheque / transaction reference"
                                        />
                                    </div>

                                </div>

                                <div className="alert alert-info mt-4 mb-0">
                                    <i className="fas fa-info-circle me-2" />
                                    This payment will settle the vendor advance against
                                    the selected Purchase Order.
                                </div>

                            </div>

                            <div className="modal-footer">

                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={closePaymentModal}
                                >
                                    Cancel
                                </button>

                                <button
                                    type="button"
                                    className="btn btn-primary"
                                    disabled={
                                        !paymentAmount ||
                                        Number(paymentAmount) <= 0 ||
                                        Number(paymentAmount) >
                                            Number(
                                                selectedVendorAdvance.outstanding_amount || 0
                                            )
                                    }
                                    onClick={handleContinueVendorAdvancePayment}
                                >
                                    <i className="fas fa-check me-1" />
                                    Continue Payment
                                </button>

                            </div>

                        </div>
                    </div>
                </div>
            )}

            {/* ======================================================
                PAYMENT HISTORY MODAL
            ====================================================== */}

            {showHistoryModal && selectedPayable && (

                <div
                    className="modal fade show d-block"
                    tabIndex="-1"
                    style={{
                        backgroundColor:
                            "rgba(0,0,0,0.5)",
                    }}
                >

                    <div className="modal-dialog modal-xl modal-dialog-centered">

                        <div className="modal-content">

                            <div className="modal-header">

                                <h5 className="modal-title">

                                    <i className="fas fa-history me-2" />

                                    Payment History

                                </h5>

                                <button
                                    type="button"
                                    className="btn-close"
                                    onClick={
                                        closeHistoryModal
                                    }
                                />

                            </div>

                            <div className="modal-body">

                                <div className="row g-3 mb-4">

                                    <div className="col-md-4">

                                        <div className="text-muted small">

                                            Vendor

                                        </div>

                                        <div className="fw-semibold">

                                            {
                                                //selectedPayable.vendor_name
                                                selectedPayable?.vendor_name ||
                                                selectedVendorAdvance?.vendor_name ||
                                                ""
                                            }

                                        </div>

                                    </div>

                                    <div className="col-md-4">

                                        <div className="text-muted small">

                                            Purchase Invoice

                                        </div>

                                        <div className="fw-semibold">

                                            {
                                                //selectedPayable.invoice_no
                                                selectedPayable?.invoice_no ||
                                                "Vendor Advance"
                                            }

                                        </div>

                                    </div>

                                    <div className="col-md-4">

                                        <div className="text-muted small">

                                            Outstanding

                                        </div>

                                        <div className="fw-semibold text-danger">

                                            {
                                                formatCurrency(
                                                    selectedPayable.outstanding_amount,
                                                    selectedPayable?.currency_code ||
                                                        selectedVendorAdvance?.currency_code ||
                                                        ""
                                                )
                                            }

                                        </div>

                                    </div>

                                </div>


                                <div className="table-responsive">

                                    <table className="table table-bordered align-middle">

                                        <thead className="table-light">

                                            <tr>

                                                <th>
                                                    Payment Voucher
                                                </th>

                                                <th>
                                                    Voucher Date
                                                </th>

                                                <th>
                                                    Allocation Date
                                                </th>

                                                <th className="text-end">
                                                    Amount
                                                </th>

                                                <th>
                                                    Status
                                                </th>

                                            </tr>

                                        </thead>

                                        <tbody>

                                            {selectedPayable?.payment_history?.length > 0 ? (
                                                selectedPayable.payment_history.map((payment) => (
                                                    <tr key={payment.allocation_id}>
                                                        <td>
                                                            {payment.voucher_no || `PV-${payment.payment_voucher_id}`}
                                                        </td>

                                                        <td>
                                                            {payment.voucher_date || "-"}
                                                        </td>

                                                        <td>
                                                            {payment.allocation_date || "-"}
                                                        </td>

                                                        <td style={{ textAlign: "right" }}>
                                                            ₹
                                                            {Number(
                                                                payment.allocated_amount || 0
                                                            ).toLocaleString("en-IN", {
                                                                minimumFractionDigits: 2,
                                                                maximumFractionDigits: 2,
                                                            })}
                                                        </td>

                                                        <td>
                                                            {payment.payment_status || "-"}
                                                        </td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td
                                                        colSpan="5"
                                                        style={{
                                                            textAlign: "center",
                                                            padding: "24px",
                                                            color: "#666",
                                                        }}
                                                    >
                                                        No payment history available.
                                                    </td>
                                                </tr>
                                            )}

                                        </tbody>

                                    </table>

                                                              </div>

                                <div className="mt-4">

                                    <h6 className="fw-semibold mb-3">
                                        Advance Adjustment History
                                    </h6>

                                    <div className="table-responsive">

                                        <table className="table table-sm table-bordered">

                                            <thead>
                                                <tr>
                                                    <th>Adjustment ID</th>
                                                    <th>Adjustment Date</th>
                                                    <th className="text-end">Amount</th>
                                                    <th>Status</th>
                                                    <th>Journal</th>
                                                    <th>Remarks</th>
                                                </tr>
                                            </thead>

                                            <tbody>

                                                {selectedPayable?.advance_adjustment_history?.length > 0 ? (

                                                    selectedPayable.advance_adjustment_history.map(
                                                        (adjustment) => (
                                                            <tr key={adjustment.adjustment_id}>

                                                                <td>
                                                                    #{adjustment.adjustment_id}
                                                                </td>

                                                                <td>
                                                                    {adjustment.adjustment_date || "-"}
                                                                </td>

                                                                <td className="text-end">
                                                                    ₹
                                                                    {Number(
                                                                        adjustment.adjustment_amount || 0
                                                                    ).toLocaleString("en-IN", {
                                                                        minimumFractionDigits: 2,
                                                                        maximumFractionDigits: 2,
                                                                    })}
                                                                </td>

                                                                <td>
                                                                    {adjustment.status || "-"}
                                                                </td>

                                                                <td>
                                                                    {adjustment.journal_header_id
                                                                        ? `Journal #${adjustment.journal_header_id}`
                                                                        : "-"}
                                                                </td>

                                                                <td>
                                                                    {adjustment.remarks || "-"}
                                                                </td>

                                                            </tr>
                                                        )
                                                    )

                                                ) : (

                                                    <tr>
                                                        <td
                                                            colSpan="6"
                                                            className="text-center text-muted"
                                                        >
                                                            No advance adjustment history available.
                                                        </td>
                                                    </tr>

                                                )}

                                            </tbody>

                                        </table>

                                    </div>

                                </div>

                            </div>


                            <div className="modal-footer">

                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={
                                        closeHistoryModal
                                    }
                                >

                                    Close

                                </button>

                            </div>

                        </div>

                    </div>

                </div>

            )}

            {/* ======================================================
                VENDOR ADVANCE VIEW MODAL
            ====================================================== */}

            {showVendorAdvanceViewModal && selectedVendorAdvanceView && (

                <div
                    className="modal fade show d-block"
                    tabIndex="-1"
                    style={{
                        backgroundColor: "rgba(0,0,0,0.5)",
                    }}
                >

                    <div className="modal-dialog modal-lg modal-dialog-centered">

                        <div className="modal-content">

                            <div className="modal-header">

                                <h5 className="modal-title">

                                    <i className="fas fa-file-invoice-dollar me-2" />

                                    Vendor Advance Details

                                </h5>

                                <button
                                    type="button"
                                    className="btn-close"
                                    onClick={() => {
                                        setShowVendorAdvanceViewModal(false);
                                        setSelectedVendorAdvanceView(null);
                                    }}
                                />

                            </div>


                            <div className="modal-body">

                                <div className="row g-3">

                                    <div className="col-md-6">

                                        <div className="text-muted small">
                                            Vendor
                                        </div>

                                        <div className="fw-semibold">
                                            {selectedVendorAdvanceView.vendor_name ||
                                                `Vendor #${selectedVendorAdvanceView.vendor_id}`}
                                        </div>

                                    </div>


                                    <div className="col-md-6">

                                        <div className="text-muted small">
                                            Purchase Order
                                        </div>

                                        <div className="fw-semibold">
                                            {selectedVendorAdvanceView.po_no ||
                                                `PO #${selectedVendorAdvanceView.purchase_order_id}`}
                                        </div>

                                    </div>


                                    <div className="col-md-4">

                                        <div className="text-muted small">
                                            Advance Type
                                        </div>

                                        <div className="fw-semibold">
                                            {selectedVendorAdvanceView.advance_type || "-"}
                                        </div>

                                    </div>


                                    <div className="col-md-4">

                                        <div className="text-muted small">
                                            Advance Percentage
                                        </div>

                                        <div className="fw-semibold">
                                            {selectedVendorAdvanceView.advance_percentage !== null &&
                                            selectedVendorAdvanceView.advance_percentage !== undefined
                                                ? `${selectedVendorAdvanceView.advance_percentage}%`
                                                : "-"}
                                        </div>

                                    </div>


                                    <div className="col-md-4">

                                        <div className="text-muted small">
                                            Currency
                                        </div>

                                        <div className="fw-semibold">
                                            {selectedVendorAdvanceView.currency_code || "INR"}
                                        </div>

                                    </div>


                                    <div className="col-md-6">

                                        <div className="text-muted small">
                                            Advance Amount
                                        </div>

                                        <div className="fw-semibold">
                                            {formatCurrency(
                                                selectedVendorAdvanceView.advance_amount,
                                                selectedVendorAdvanceView.currency_code || "INR"
                                            )}
                                        </div>

                                    </div>


                                    <div className="col-md-6">

                                        <div className="text-muted small">
                                            Paid Amount
                                        </div>

                                        <div className="fw-semibold">
                                            {formatCurrency(
                                                selectedVendorAdvanceView.paid_amount,
                                                selectedVendorAdvanceView.currency_code || "INR"
                                            )}
                                        </div>

                                    </div>


                                    <div className="col-md-6">

                                        <div className="text-muted small">
                                            Adjusted Amount
                                        </div>

                                        <div className="fw-semibold">
                                            {formatCurrency(
                                                selectedVendorAdvanceView.adjusted_amount,
                                                selectedVendorAdvanceView.currency_code || "INR"
                                            )}
                                        </div>

                                    </div>


                                    <div className="col-md-6">

                                        <div className="text-muted small">
                                            Outstanding Amount
                                        </div>

                                        <div className="fw-semibold text-danger">
                                            {formatCurrency(
                                                selectedVendorAdvanceView.outstanding_amount,
                                                selectedVendorAdvanceView.currency_code || "INR"
                                            )}
                                        </div>

                                    </div>


                                    <div className="col-md-6">

                                        <div className="text-muted small">
                                            Status
                                        </div>

                                        <div>
                                            <span className="badge bg-warning text-dark">
                                                {selectedVendorAdvanceView.status
                                                    ?.replace("_", " ") || "-"}
                                            </span>
                                        </div>

                                    </div>


                                    <div className="col-md-6">

                                        <div className="text-muted small">
                                            Payment Voucher
                                        </div>

                                        <div className="fw-semibold">
                                            {selectedVendorAdvanceView.payment_voucher_id
                                                ? `PV-${selectedVendorAdvanceView.payment_voucher_id}`
                                                : "-"}
                                        </div>

                                    </div>

                                </div>

                            </div>


                            <div className="modal-footer">

                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={() => {
                                        setShowVendorAdvanceViewModal(false);
                                        setSelectedVendorAdvanceView(null);
                                    }}
                                >
                                    Close
                                </button>

                            </div>

                        </div>

                    </div>

                </div>

            )}
            #######
            {showAdjustmentModal && selectedAdjustmentPayable && (
                <div
                    className="modal fade show"
                    style={{
                        display: "block",
                        backgroundColor: "rgba(0,0,0,0.5)",
                    }}
                >
                    <div className="modal-dialog modal-lg modal-dialog-centered">
                        <div className="modal-content">

                            <div className="modal-header">
                                <h5 className="modal-title">
                                    Adjust Vendor Advance
                                </h5>

                                <button
                                    type="button"
                                    className="btn-close"
                                    onClick={() =>
                                        setShowAdjustmentModal(false)
                                    }
                                />
                            </div>

                            <div className="modal-body">

                                <div className="mb-3">
                                    <strong>
                                        Purchase Invoice:
                                    </strong>{" "}
                                    {selectedAdjustmentPayable.invoice_no}
                                </div>

                                <div className="mb-3">
                                    <strong>
                                        Vendor:
                                    </strong>{" "}
                                    {selectedAdjustmentPayable.vendor_name}
                                </div>

                                <div className="mb-3">
                                    <strong>
                                        Available Adjustment:
                                    </strong>{" "}
                                    {selectedAdjustmentAdvance
                                        ? formatCurrency(
                                            selectedAdjustmentAdvance.available_adjustment_amount,
                                            selectedAdjustmentPayable.currency_code
                                        )
                                        : "-"}
                                </div>

                                <hr />

                                <h6 className="mb-3">
                                    Available Vendor Advances
                                </h6>

                                {adjustableVendorAdvances.length === 0 ? (
                                    <div className="alert alert-info">
                                        No outstanding Vendor Advance is available
                                        for adjustment.
                                    </div>
                                ) : (
                                    <div className="table-responsive">
                                        <table className="table table-sm table-bordered">
                                            <thead>
                                                <tr>
                                                    <th>PO</th>
                                                    <th>Advance Type</th>
                                                    <th>Advance Amount</th>
                                                    <th>Paid</th>
                                                    <th>Adjusted</th>
                                                    <th>Outstanding</th>
                                                    <th>Select</th>
                                                </tr>
                                            </thead>

                                            <tbody>
                                                {adjustableVendorAdvances.map(
                                                    (advance) => (
                                                        <tr
                                                            key={
                                                                advance.vendor_advance_id
                                                            }
                                                        >
                                                            <td>
                                                                {advance.purchase_order_id}
                                                            </td>

                                                            <td>
                                                                {advance.advance_type}
                                                            </td>

                                                            <td>
                                                                {formatCurrency(
                                                                    advance.advance_amount,
                                                                    advance.currency_code
                                                                )}
                                                            </td>

                                                            <td>
                                                                {formatCurrency(
                                                                    advance.paid_amount,
                                                                    advance.currency_code
                                                                )}
                                                            </td>

                                                            <td>
                                                                {formatCurrency(
                                                                    advance.adjusted_amount,
                                                                    advance.currency_code
                                                                )}
                                                            </td>

                                                            <td>
                                                                {formatCurrency(
                                                                    advance.outstanding_amount,
                                                                    advance.currency_code
                                                                )}
                                                            </td>

                                                            <td>
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-sm btn-outline-primary"
                                                                    onClick={() =>
                                                                        setSelectedAdjustmentAdvance(
                                                                            advance
                                                                        )
                                                                    }
                                                                >
                                                                    Select
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    )
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                )}

                                {selectedAdjustmentAdvance && (
                                    <>
                                        <hr />

                                        <div className="mb-3">
                                            <strong>
                                                Selected Advance:
                                            </strong>{" "}
                                            #{selectedAdjustmentAdvance.vendor_advance_id}
                                        </div>

                                        <div className="mb-3">
                                            <label className="form-label">
                                                Adjustment Amount
                                            </label>

                                            <input
                                                type="number"
                                                className="form-control"
                                                value={adjustmentAmount}
                                                onChange={(e) => setAdjustmentAmount(e.target.value)}
                                                max={
                                                    selectedAdjustmentAdvance
                                                        ? Math.min(
                                                            Number(
                                                                selectedAdjustmentAdvance.available_adjustment_amount || 0
                                                            ),
                                                            Number(
                                                                selectedAdjustmentPayable?.outstanding_amount || 0
                                                            )
                                                        )
                                                        : undefined
                                                }
                                            />
                                        </div>

                                        <div className="mb-3">
                                            <label className="form-label">
                                                Adjustment Date
                                            </label>

                                            <input
                                                type="date"
                                                className="form-control"
                                                value={adjustmentDate}
                                                onChange={(e) =>
                                                    setAdjustmentDate(
                                                        e.target.value
                                                    )
                                                }
                                            />
                                        </div>

                                        <div className="mb-3">
                                            <label className="form-label">
                                                Remarks
                                            </label>

                                            <textarea
                                                className="form-control"
                                                value={adjustmentRemarks}
                                                onChange={(e) =>
                                                    setAdjustmentRemarks(
                                                        e.target.value
                                                    )
                                                }
                                                rows="3"
                                            />
                                        </div>
                                    </>
                                )}
                            </div>

                            <div className="modal-footer">

                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={() =>
                                        setShowAdjustmentModal(false)
                                    }
                                >
                                    Close
                                </button>

                                <button
                                    type="button"
                                    className="btn btn-primary"
                                    disabled={
                                        !selectedAdjustmentAdvance
                                    }
                                    onClick={handleSubmitAdjustment}
                                >
                                    Adjust Advance
                                </button>

                            </div>

                        </div>
                    </div>
                </div>
            )}
        </div>

    );

}